import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthState, LoginCredentials, SignupCredentials, GoogleCredentialResponse } from '../types/Auth';
import { apiClient, ApiError } from '../services/api';

interface AuthStore extends AuthState {
    login: (credentials: LoginCredentials) => Promise<void>;
    loginWithGoogle: (credentialResponse: GoogleCredentialResponse) => Promise<void>;
    register: (credentials: SignupCredentials) => Promise<void>;
    logout: () => void;
    clearError: () => void;
    loadUserFromToken: () => Promise<void>;
}



// Helper function to decode JWT token (simplified for demo)
const decodeJWT = (token: string) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Failed to decode JWT:', error);
        return null;
    }
};

// Helper function to validate password strength
const validatePasswordStrength = (password: string): string | null => {
    if (password.length < 8) {
        return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
        return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
        return 'Password must contain at least one lowercase letter';
    }
    if (!/\d/.test(password)) {
        return 'Password must contain at least one number';
    }
    return null;
};

export const useAuth = create<AuthStore>()(
    persist(
        (set) => ({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,

            login: async (credentials: LoginCredentials) => {
                set({ isLoading: true, error: null });

                try {
                    const response = await apiClient.login(credentials);
                    
                    set({
                        user: response.user,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null
                    });
                } catch (error) {
                    console.error('Login error:', error);
                    let errorMessage = 'Login failed. Please try again.';
                    
                    if (error instanceof ApiError) {
                        errorMessage = error.message;
                        // Check for email verification error
                        if ((error.data as { code?: string })?.code === 'EMAIL_NOT_VERIFIED') {
                            errorMessage = 'Email address not verified. Please check your email for verification link.';
                        }
                    }
                    
                    set({
                        error: errorMessage,
                        isLoading: false
                    });
                }
            },

            loginWithGoogle: async (credentialResponse: GoogleCredentialResponse) => {
                set({ isLoading: true, error: null });

                try {
                    // Note: Google OAuth integration with backend needs to be implemented
                    // For now, we'll decode the token and create a user
                    const decoded = decodeJWT(credentialResponse.credential);
                    
                    if (!decoded) {
                        throw new Error('Failed to decode Google credential');
                    }

                    // TODO: Send Google credential to backend for verification and user creation/login
                    // This is a placeholder implementation
                    console.warn('Google OAuth backend integration not yet implemented');
                    
                    set({
                        error: 'Google OAuth not yet integrated with backend. Please use email/password login.',
                        isLoading: false
                    });
                } catch (error) {
                    console.error('Google login error:', error);
                    set({
                        error: 'Google login failed. Please try again with email/password.',
                        isLoading: false
                    });
                }
            },

            register: async (credentials: SignupCredentials) => {
                set({ isLoading: true, error: null });

                try {
                    // Validate password strength
                    const passwordError = validatePasswordStrength(credentials.password);
                    if (passwordError) {
                        set({
                            error: passwordError,
                            isLoading: false
                        });
                        return;
                    }

                    // Check if passwords match
                    if (credentials.password !== credentials.confirmPassword) {
                        set({
                            error: 'Passwords do not match',
                            isLoading: false
                        });
                        return;
                    }

                    const response = await apiClient.signup(credentials);
                    
                    set({
                        user: response.user,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null
                    });
                } catch (error) {
                    console.error('Registration error:', error);
                    let errorMessage = 'Registration failed. Please try again.';
                    
                    if (error instanceof ApiError) {
                        errorMessage = error.message;
                    }
                    
                    set({
                        error: errorMessage,
                        isLoading: false
                    });
                }
            },

            logout: async () => {
                set({ isLoading: true });
                
                try {
                    await apiClient.logout();
                } catch (error) {
                    console.error('Logout error:', error);
                    // Continue with logout even if server request fails
                }

                set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                    error: null
                });
            },

            clearError: () => {
                set({ error: null });
            },

            loadUserFromToken: async () => {
                const token = localStorage.getItem('auth-token');
                if (!token) return;

                set({ isLoading: true });

                try {
                    const response = await apiClient.getProfile();
                    set({
                        user: response.user,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null
                    });
                } catch (error) {
                    console.error('Failed to load user from token:', error);
                    // Clear invalid token
                    localStorage.removeItem('auth-token');
                    set({
                        user: null,
                        isAuthenticated: false,
                        isLoading: false,
                        error: null
                    });
                }
            },
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                isAuthenticated: state.isAuthenticated
            }),
            onRehydrateStorage: () => (state) => {
                // Load user data from token after rehydration
                if (state && localStorage.getItem('auth-token')) {
                    state.loadUserFromToken();
                }
            },
        }
    )
); 